import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { paymentApi, Transaction } from '../../api/paymentApi';
import { getApiErrorMessage } from '../../utils/apiErrors';

interface TransactionBillCancelModalProps {
  isOpen: boolean;
  transaction: Transaction | null;
  onClose: () => void;
  onCancelled: () => void;
}

const formatVnd = (n: number) => `${Math.round(n).toLocaleString('vi-VN')} đ`;

export const TransactionBillCancelModal: React.FC<TransactionBillCancelModalProps> = ({
  isOpen,
  transaction,
  onClose,
  onCancelled,
}) => {
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setNotes('');
    }
  }, [isOpen, transaction?.id]);

  if (!isOpen || !transaction) return null;

  const receiptLabel =
    transaction.receiptNumber?.trim() || transaction.billId?.trim() || transaction.id;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    try {
      await paymentApi.cancelTransactionBill(transaction.id, notes.trim() || undefined);
      toast.success('Đã huỷ phiếu thu');
      onCancelled();
      onClose();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Không thể huỷ phiếu thu'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <form onSubmit={handleSubmit}>
          <div className="border-b px-5 py-4">
            <h2 className="text-lg font-semibold text-gray-900">Huỷ phiếu thu</h2>
            <p className="text-sm text-gray-600 mt-1">
              Phiếu <strong>{receiptLabel}</strong> · {transaction.apartmentCode} ·{' '}
              {formatVnd(transaction.amount)}
            </p>
          </div>
          <div className="px-5 py-4 space-y-3">
            <p className="text-sm text-gray-700">
              Các khoản phí trên phiếu sẽ trở lại chưa thanh toán. Số tiền thu sẽ trừ khỏi tài khoản
              dự án. Thẻ xe vẫn giữ trạng thái đã bán nếu còn hóa đơn thẻ chưa thanh toán.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ghi chú</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                placeholder="Lý do huỷ phiếu (tuỳ chọn)"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 border-t px-5 py-4">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Đóng
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50"
            >
              {saving ? 'Đang huỷ...' : 'Xác nhận huỷ'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
